import { useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import { normalizeProfileName } from '../lib/profile-rules'
import type { ParticipantProfile, SaveProfileResponse } from '../lib/types'
import { useSession } from '../lib/session'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { useToast } from '../ui/Feedback'

interface ParticipantProfileFormProps {
  profile: ParticipantProfile
  submitLabel: string
  onSaved?: (result: SaveProfileResponse) => void
  /**
   * Show the track as settled text rather than a picker.
   *
   * Set on the Account screen, where changing track after check-ins have accumulated
   * corrupts the certificate maths: checkCertStatus takes the denominator from the
   * participant's CURRENT track's qr_required slots, while checkinMoreThanPercent
   * counts every check-in they have regardless of which track's slot it was. Twelve
   * check-ins carried onto a ten-slot track read as 120% and grant a certificate on
   * the spot. The first-login sheet leaves this off, because that is where the choice
   * is legitimately made.
   *
   * Ignored when the profile has no track yet, so an incomplete profile can still be
   * completed rather than being locked out of the only field that would fix it.
   */
  lockTrack?: boolean
}

export function ParticipantProfileForm({
  profile,
  submitLabel,
  onSaved,
  lockTrack = false,
}: ParticipantProfileFormProps) {
  const { session, patch } = useSession()
  const toast = useToast()
  const [name, setName] = useState(profile.full_name)
  const [trackId, setTrackId] = useState(profile.track_id ? String(profile.track_id) : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setName(profile.full_name)
    setTrackId(profile.track_id ? String(profile.track_id) : '')
  }, [profile.full_name, profile.track_id])

  async function save(event: FormEvent) {
    event.preventDefault()
    const fullName = normalizeProfileName(name)
    const selectedTrackId = lockTrack && profile.track_id ? profile.track_id : Number(trackId)
    if (fullName.length < 2) {
      setError('Please enter your full name.')
      return
    }
    if (!Number.isInteger(selectedTrackId) || selectedTrackId < 1) {
      setError('Please select your track.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await api.saveParticipantProfile(
        session!.email,
        session!.at,
        fullName,
        selectedTrackId,
      )
      if (!result.is_success) {
        setError('Your profile could not be saved. Try again.')
        return
      }
      patch({
        name: result.full_name,
        track_id: result.track_id,
        track_name: result.track_name,
        verified_name: result.verified_name,
      })
      toast.show('Profile saved', 'ok')
      onSaved?.(result)
    } catch (err) {
      /* The server's own words for a 409: the name is locked, and retrying will not help. */
      const msg = err instanceof ApiError && err.status === 409 && typeof err.body?.msg === 'string'
        ? err.body.msg
        : 'Could not reach the server. Try again in a moment.'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="profile-form" onSubmit={(event) => void save(event)}>
      <Field
        label="Full name for certificate"
        autoComplete="name"
        autoCapitalize="words"
        value={name}
        onChange={(event) => setName(event.target.value)}
        hint="Check the spelling, titles and order of your name."
        required
      />
      {lockTrack && profile.track_id ? (
        <div className="field profile-form__locked">
          <span className="field__label kicker">Track</span>
          <p className="profile-form__locked-value">{profile.track_name || 'Your track'}</p>
        </div>
      ) : (
      <label className="field">
        <span className="field__label kicker">Track</span>
        <select
          className="field__input profile-form__select"
          value={trackId}
          onChange={(event) => setTrackId(event.target.value)}
          required
          aria-describedby="profile-track-hint"
        >
          <option value="">Select your track</option>
          {profile.tracks.map((track) => (
            <option value={track.id} key={track.id}>{track.track_name}</option>
          ))}
        </select>
        <span id="profile-track-hint" className="field__hint">
          Your programme and attendance requirement follow this selection.
        </span>
      </label>
      )}
      {error ? <p className="field__error" role="alert">{error}</p> : null}
      <Button type="submit" variant="primary" busy={busy} disabled={profile.tracks.length === 0}>
        {submitLabel}
      </Button>
    </form>
  )
}
